import { useEffect, useRef, useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, MapPin, Loader2 } from 'lucide-react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLocationAutoSave } from './hooks/useLocationAutoSave';
import { getMapStyleUrl } from '@/api/geo';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

export function GpsDiagnosticsPage() {
  const {
    isSupported,
    isSaving,
    lastSaveAt,
    nextSaveAt,
    currentPosition,
    error,
    permissionState,
    refresh,
  } = useLocationAutoSave();

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyleUrl(),
      center: [27.5615, 53.9045], // Minsk default
      zoom: 13,
      attributionControl: false,
    });

    map.current.on('load', () => {
      setMapLoaded(true);

      // Add accuracy circle source
      map.current!.addSource('accuracy-circle', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [0, 0] },
          properties: {},
        },
      });

      // Add accuracy circle layer
      map.current!.addLayer({
        id: 'accuracy-circle-fill',
        type: 'circle',
        source: 'accuracy-circle',
        paint: {
          'circle-radius': 0,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.2,
          'circle-stroke-color': '#3b82f6',
          'circle-stroke-width': 2,
          'circle-stroke-opacity': 0.5,
        },
      });
    });

    map.current.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right'
    );

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Update map with position
  useEffect(() => {
    if (!map.current || !mapLoaded || !currentPosition) return;

    const { latitude, longitude, accuracy } = currentPosition.coords;

    // Update or create marker
    if (!marker.current) {
      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          width: 20px;
          height: 20px;
          background: #3b82f6;
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        "></div>
      `;
      marker.current = new maplibregl.Marker({ element: el })
        .setLngLat([longitude, latitude])
        .addTo(map.current);
    } else {
      marker.current.setLngLat([longitude, latitude]);
    }

    // Update accuracy circle
    const source = map.current.getSource('accuracy-circle') as maplibregl.GeoJSONSource;
    if (source) {
      source.setData({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [longitude, latitude] },
        properties: {},
      });

      // Calculate circle radius in pixels based on accuracy
      const metersPerPixel =
        (156543.03392 * Math.cos((latitude * Math.PI) / 180)) /
        Math.pow(2, map.current.getZoom());
      const radiusPixels = accuracy / metersPerPixel;

      map.current.setPaintProperty('accuracy-circle-fill', 'circle-radius', radiusPixels);
    }

    // Fly to position
    map.current.flyTo({
      center: [longitude, latitude],
      zoom: 15,
      duration: 1000,
    });
  }, [currentPosition, mapLoaded]);

  const getPermissionIcon = () => {
    switch (permissionState) {
      case 'granted':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'denied':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'prompt':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getPermissionText = () => {
    switch (permissionState) {
      case 'granted':
        return 'Разрешено';
      case 'denied':
        return 'Запрещено';
      case 'prompt':
        return 'Ожидает разрешения';
      default:
        return 'Неизвестно';
    }
  };

  const getAccuracyLabel = (accuracy: number) => {
    if (accuracy <= 10) return 'Отличная';
    if (accuracy <= 30) return 'Хорошая';
    if (accuracy <= 100) return 'Средняя';
    return 'Низкая';
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy <= 10) return 'text-green-600';
    if (accuracy <= 30) return 'text-green-500';
    if (accuracy <= 100) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="flex flex-col min-h-full">
      <Header
        title="GPS Диагностика"
        showBack
        rightContent={
          <Button variant="ghost" size="icon" onClick={refresh} disabled={isSaving}>
            <RefreshCw className={`h-5 w-5 ${isSaving ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Status Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Статус GPS
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-2">
              {isSupported ? (
                <>
                  {getPermissionIcon()}
                  <span className="text-sm">{getPermissionText()}</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-destructive" />
                  <span className="text-sm">Не поддерживается</span>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Точность
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-2">
              {currentPosition ? (
                <>
                  <MapPin
                    className={`h-5 w-5 ${getAccuracyColor(currentPosition.coords.accuracy)}`}
                  />
                  <span className="text-sm">
                    {getAccuracyLabel(currentPosition.coords.accuracy)} (
                    {Math.round(currentPosition.coords.accuracy)}м)
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">—</span>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Current Position Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Текущие координаты
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentPosition ? (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Широта:</span>
                  <span className="font-mono">
                    {currentPosition.coords.latitude.toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Долгота:</span>
                  <span className="font-mono">
                    {currentPosition.coords.longitude.toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Точность:</span>
                  <span className="font-mono">
                    {currentPosition.coords.accuracy.toFixed(0)} м
                  </span>
                </div>
                {currentPosition.coords.altitude !== null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Высота:</span>
                    <span className="font-mono">
                      {currentPosition.coords.altitude.toFixed(0)} м
                    </span>
                  </div>
                )}
                {currentPosition.coords.speed !== null && currentPosition.coords.speed > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Скорость:</span>
                    <span className="font-mono">
                      {(currentPosition.coords.speed * 3.6).toFixed(1)} км/ч
                    </span>
                  </div>
                )}
              </div>
            ) : error ? (
              <div className="text-sm text-destructive">
                Ошибка: {error.message || `Код ${error.code}`}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Определение местоположения...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="overflow-hidden">
          <div ref={mapContainer} className="h-64" />
        </Card>

        {/* Save Status Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Сохранение на сервер</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Последнее сохранение:</span>
              <span>
                {lastSaveAt
                  ? formatDistanceToNow(lastSaveAt, { addSuffix: true, locale: ru })
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Следующее возможно:</span>
              <span>
                {nextSaveAt && nextSaveAt > new Date()
                  ? formatDistanceToNow(nextSaveAt, { addSuffix: true, locale: ru })
                  : 'сейчас'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Retry Button */}
        <Button onClick={refresh} disabled={isSaving} className="w-full">
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Сохранение...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Обновить местоположение
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
